import { z } from "zod";

// TES-1676 — admin-written announcement campaigns, "Utskick" in admin.
//
// One campaign is one push to one patient segment (the TES-1507 engine,
// apps/api/src/notifications/announcement.ts) plus the copy of the Hem card
// its recipients see (S2), and optionally a free first care meeting for
// them until a date (Simon 2026-11-03: no offer code, the server waives the
// fee). Admin only. Every count here is a count: no patient id, name or
// address ever reaches this surface.

export const ANNOUNCEMENT_CAMPAIGN_SEGMENTS = [
  "f01_not_done",
  "f01_done_not_booked",
] as const;
export type AnnouncementCampaignSegment =
  (typeof ANNOUNCEMENT_CAMPAIGN_SEGMENTS)[number];

/** Lower-case, digits and dashes, no colon: the key is part of the dedupe key. */
export const ANNOUNCEMENT_CAMPAIGN_KEY_RE = /^[a-z0-9][a-z0-9-]{2,62}$/;

// Hard limits. A push body longer than about 178 characters is cut on iOS
// (the admin page shows that as a hint, not a refusal). The Hem card body
// holds the full message (Zeinab's approved text is about 400 characters).
export const ANNOUNCEMENT_LIMITS = {
  pushTitle: 65,
  pushBody: 240,
  pushBodyVisible: 178,
  cardTitle: 80,
  cardBody: 1000,
  buttonLabel: 40,
} as const;

/** The copy a send needs, in the order the page lists it. */
export const ANNOUNCEMENT_REQUIRED_COPY = [
  "pushTitle",
  "pushBody",
  "cardTitle",
  "cardBody",
  "buttonLabel",
] as const;
export type AnnouncementCopyField = (typeof ANNOUNCEMENT_REQUIRED_COPY)[number];

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

// Simon 2026-11-03 — a campaign reaches every build unless it is limited
// to a minimum and/or maximum app version.
//
// The api knows a patient's build only from the contract date her app
// sends on every request (x-ella-contract → Patient.lastClientContract,
// written on the 60-second unread poll every build makes). It never hears
// the marketing version after the first open (AppInstall.appVersion is the
// version at install, not today's). So a version is the first contract date
// its builds claim, read off git: release/2.0.11 claims 2026-10-16 (as 2.0.9
// and 2.0.10 did, so the three cannot be told apart), and main claimed
// 2026-11-03 when it was bumped to 2.1.0 (#1754). Only versions with their
// own boundary are listed; a contract before the first one is an older
// build. Internal builds between the two (2026-10-18 to 2026-11-02, QA and
// TestFlight only) count as 2.0.11.
export const ANNOUNCEMENT_APP_VERSIONS = [
  { version: "2.0.11", firstContract: "2026-10-16" },
  { version: "2.1.0", firstContract: "2026-11-03" },
] as const;
export type AnnouncementAppVersion =
  (typeof ANNOUNCEMENT_APP_VERSIONS)[number]["version"];
const APP_VERSIONS = ANNOUNCEMENT_APP_VERSIONS.map((v) => v.version) as [
  AnnouncementAppVersion,
  ...AnnouncementAppVersion[],
];
const versionIndex = (v: AnnouncementAppVersion) => APP_VERSIONS.indexOf(v);

export interface AnnouncementBuildRange {
  minAppVersion: AnnouncementAppVersion | null;
  maxAppVersion: AnnouncementAppVersion | null;
}

/**
 * Whether her build is inside the range. With no limit every build is in,
 * an unknown one too; with a limit, a build that never reported a contract
 * is `unknown` and is not reached.
 */
export function buildInRange(
  contract: string | null,
  range: AnnouncementBuildRange,
): "in" | "outside" | "unknown" {
  if (range.minAppVersion === null && range.maxAppVersion === null) {
    return "in";
  }
  if (contract === null) return "unknown";
  // -1: older than every listed version.
  let at = -1;
  ANNOUNCEMENT_APP_VERSIONS.forEach((v, i) => {
    if (v.firstContract <= contract) at = i;
  });
  if (range.minAppVersion !== null && at < versionIndex(range.minAppVersion)) {
    return "outside";
  }
  if (range.maxAppVersion !== null && at > versionIndex(range.maxAppVersion)) {
    return "outside";
  }
  return "in";
}

/** Create or update a draft. Copy may be empty in a draft; send refuses it. */
export const announcementCampaignDraftSchema = z
  .object({
    key: z.string().regex(ANNOUNCEMENT_CAMPAIGN_KEY_RE),
    segment: z.enum(ANNOUNCEMENT_CAMPAIGN_SEGMENTS),
    pushTitle: z.string().max(ANNOUNCEMENT_LIMITS.pushTitle),
    pushBody: z.string().max(ANNOUNCEMENT_LIMITS.pushBody),
    cardTitle: z.string().max(ANNOUNCEMENT_LIMITS.cardTitle),
    cardBody: z.string().max(ANNOUNCEMENT_LIMITS.cardBody),
    buttonLabel: z.string().max(ANNOUNCEMENT_LIMITS.buttonLabel),
    /** "Kostnadsfritt vårdmöte till och med", inclusive; null for no waiver. */
    freeMeetingUntil: isoDate.nullable(),
    /** Null: no limit on that side. Both null: every build. */
    minAppVersion: z.enum(APP_VERSIONS).nullable(),
    maxAppVersion: z.enum(APP_VERSIONS).nullable(),
  })
  .refine(
    (d) =>
      d.minAppVersion === null ||
      d.maxAppVersion === null ||
      versionIndex(d.minAppVersion) <= versionIndex(d.maxAppVersion),
    { path: ["maxAppVersion"], message: "max_below_min" },
  );

/** The build range of a dry-run, from its query. */
export const announcementBuildRangeSchema = z.object({
  minAppVersion: z.enum(APP_VERSIONS).nullable().default(null),
  maxAppVersion: z.enum(APP_VERSIONS).nullable().default(null),
});
export type AnnouncementCampaignDraft = z.infer<
  typeof announcementCampaignDraftSchema
>;

/** Send is confirmed by typing the campaign's key. */
export const announcementCampaignSendSchema = z.object({
  confirmKey: z.string(),
});
export type AnnouncementCampaignSend = z.infer<
  typeof announcementCampaignSendSchema
>;

/** The copy fields still empty (trimmed), which a send refuses. */
export function missingAnnouncementCopy(
  c: Pick<AnnouncementCampaignDraft, AnnouncementCopyField>,
): AnnouncementCopyField[] {
  return ANNOUNCEMENT_REQUIRED_COPY.filter((f) => c[f].trim() === "");
}

/** Per segment: who is reachable, and why the others are not. */
export interface AnnouncementSegmentCounts {
  eligible: number;
  test: number;
  /** Prescription transfer (F-07): follows its own ladder. */
  transfer: number;
  /**
   * TES-1681 — F-01 done, but the first consultation's own gates would stop
   * her booking it (under 18, no grounded care path, a transfer without a
   * required meeting, an open acute flag, F-01 "Vill inte ange"). Always 0
   * in f01_not_done. Missing from the counts a campaign stored before it.
   */
  cannot_book: number;
  /**
   * TES-1681 (Simon 2026-11-04) — her latest F-01 is a stop (Resultat B).
   * She can book, but campaigns do not invite her. Always 0 in
   * f01_not_done; missing from counts stored before it.
   */
  f01_stopped: number;
  no_push_token: number;
  opted_out: number;
  /**
   * An app before the announcement toggle (2.0.11 and older). Only the box
   * script holds these back; an admin campaign reaches every build.
   */
  older_build: number;
  /** A build limit is set and her app never reported its build. */
  unknown_build: number;
  /** A build limit is set and her build is outside it. */
  outside_build: number;
}

export interface AnnouncementDryRun {
  patients: number;
  outside: { erased: number; not_in_app: number; booked: number };
  bySegment: Record<AnnouncementCampaignSegment, AnnouncementSegmentCounts>;
}

export interface AnnouncementSendCounts extends AnnouncementDryRun {
  enqueued: { scheduled: number; duplicate: number; opted_out: number };
  /** Recipients recorded for the campaign (the free meeting reads these). */
  recipients: number;
}

/**
 * TES-1687 — what came of a sent campaign, counted when the page is read.
 * Counts only, never a patient.
 */
export interface AnnouncementCampaignOutcome {
  /** Pushes the drainer sent (handed to APNs/FCM). */
  sent: number;
  /**
   * Of those, the pushes she tapped. Only an app at
   * CLIENT_CONTRACT_CHANGES.notificationTaps reports a tap, so this is a
   * floor; `reportingTaps` says how many of `sent` it can count.
   */
  opened: number;
  /**
   * Of `sent`, those whose recipient's app (as last seen) reports taps.
   * Last seen, not at the tap: an upgrade after the push counts her.
   */
  reportingTaps: number;
  /** Free first meetings opened: the campaign's 0 kr credit was created. */
  freeMeetings: number;
  /** Recipients who booked a first care meeting after the send. */
  booked: number;
}

export interface AnnouncementCampaignView extends AnnouncementCampaignDraft {
  id: string;
  status: "draft" | "sent";
  createdAt: string;
  updatedAt: string;
  sentAt: string | null;
  sendCounts: AnnouncementSendCounts | null;
  missingCopy: AnnouncementCopyField[];
  /** TES-1687 — null for a draft. */
  outcome: AnnouncementCampaignOutcome | null;
}
